<template lang="pug">
  v-dialog(v-model="show" persistent max-width="600px" scrollable :fullscreen="$vuetify.breakpoint.smAndDown" )
    v-snackbar( v-model="snackbar" :color="snackbarColor" ) {{snackBarText}}
      template( v-slot:action="{attrs}")
        v-btn(text v-bind="attrs"
          @click="snackbar = false")
          v-icon fal fa-times

    v-card.pa-0(justify="center" height="100%" v-if="!isRatingFinished")
      v-card-title.text-center {{ $t('ratingDialog.ratingOrderTitle') }}
      v-card-subtitle.pb-0
        v-row.ma-0.mt-6
          v-col.text-center(cols="12")
            v-btn(icon @click="setOpacity(1)" :style="{ opacity: ratingValue === 1 ? '1' : '0.3' }")
              v-icon(color="error" style="font-size: 60px") fas fa-grimace
            v-btn.ml-10(icon @click="setOpacity(2)" :style="{ opacity: ratingValue === 2 ? '1' : '0.3' }")
              v-icon(large  color="orange" style="font-size: 60px") fas fa-frown-open
            v-btn.ml-10(icon @click="setOpacity(3)" :style="{ opacity: ratingValue === 3 ? '1' : '0.3' }")
              v-icon(large  color="primary" style="font-size: 60px") fas fa-smile
            v-btn.ml-10(icon @click="setOpacity(4)" :style="{ opacity: ratingValue === 4 ? '1' : '0.3' }")
              v-icon(large  color="success" style="font-size: 60px") fas fa-grin-hearts
        v-row.ma-0.mt-6
          v-col.pa-0(cols="12")
            v-textarea.ma-0.pa-0(outlined :label="$t('ratingDialog.description')" rows="3" v-model="description")
      span.text-center.title.text-style.ml-6.mr-6.mb-4(dense  v-if="isDateTwoDaysBeforeToday" ) {{ $t('ratingDialog.ratingAlertTitle') }}!
      span.text-center.title.text-style.ml-6.mr-6.mb-4(dense  v-else ) {{ $t('ratingDialog.ratingAlertTitleAfterDeadLine') }}
      v-card-text.overflow-auto
        div.text-center.title {{ $t('ratingDialog.offerDetailTitle') }} {{getFormattedDate}}
        v-row.ma-0
          v-col.pa-0(cols="12")
            v-list.pa-0.ma-0
              v-list-item(v-for="(item,idx) in getFilteredData" :key="idx")
                v-list-item-content.ma-0
                  v-row.ma-0()
                    v-col.pa-0(cols="12") {{item.group}}
                    v-col.pa-0(cols="12")
                      v-row.ma-0( v-for="(i,index) in item" :key="index")
                        v-col.text-start.pa-0(cols="10" style="color:#757575") {{i.title}}
                        v-col.text-end.pa-0(cols="2" style="color:#757575") {{getAmount(i.amount)}}
      v-divider
      v-card-actions
        v-spacer
        v-btn(@click="closeDialog" text :disabled="saveInProgress") {{ $t('ratingDialog.closeDialog') }}
        v-btn(@click="sendRating" color="primary" :loading="saveInProgress" :disabled="!isDateTwoDaysBeforeToday || isSaveButtonDisabled") {{ $t('ratingDialog.sendRating') }}
    v-card(v-else)
      v-card-title.text-center {{ $t('ratingDialog.ratingOrderTitle') }}
      v-card-text
        v-row.ma-0
          v-col.text-center.title(cols="12") {{ $t('ratingDialog.finishedMessage') }}
      v-divider
      v-card-actions
        v-spacer
        v-btn(@click="closeDialog" text) {{ $t('ratingDialog.closeDialog') }}
</template>

<script lang="ts">
import Vue from 'vue'

import {Component, Prop} from 'vue-property-decorator'
import {TranslateResult} from 'vue-i18n'
import moment from "moment";
import {getLocale} from "@/services/system";
import {usePiniaStore} from "@/store/piniaStore";
import {RatingModel} from "@/model/RatingModel";
import {RatingPayload} from "@/types/payload";


@Component
export default class RatingDialog extends Vue {
  private showError: boolean = false
  private errorMessage: TranslateResult | null = null
  private saveInProgress: boolean = false
  private snackbar: boolean = false
  private snackBarText: TranslateResult = ''
  private snackbarColor: string = ''
  private ratingValue: any | null = null
  private description: string | null = null
  private piniaStore: any = usePiniaStore()
  private selectedId: string | null = null
  private offerRating: RatingModel | null = null
  private createdDate: string = ''
  private isRatingFinished: boolean = false

  @Prop({type: Boolean, default: false}) readonly show!: boolean
  @Prop({type: Array, default: false}) readonly lastReservationDetailData!: any[]
  @Prop({type: String, required: false}) readonly date!: string
  @Prop({type: Object, required: false}) readonly selectedOfferRating!: RatingModel
  @Prop({type: Array, required: false}) readonly ratingDataList!: any
  @Prop({type: Number, required: false}) readonly selectedRatingValue!: null
  @Prop({type: Object, required: false}) readonly selectedOffer!: any
  @Prop({type: Array, required: false}) readonly itemData!: any[]
  @Prop({type: Number, required: false}) readonly ratingDeliveryTimeData!: null

  get isSaveButtonDisabled ():boolean {
    let isDisabled: boolean = false
    if(this.ratingValue) {
      isDisabled = false
    } else {
      isDisabled = true
    }
    return isDisabled
  }

  setOpacity (value:any): any {
    this.ratingValue = value
  }

  getAmount (item:any) {
    if(item) {
      return item + 'x'
    } else {
      return ''
    }
  }

  get getFormattedDate (): string {
    let date: string = ''
    if(this.lastReservationDetailData && this.lastReservationDetailData.length > 0) {
      this.lastReservationDetailData.forEach((item:any) => {
        date = item.date
      })
    } else {
      date = this.selectedOffer.date
    }
    return moment(date)
      .locale(getLocale())
      .format('D.M.YYYY').toLocaleLowerCase()
  }

  get getFilteredData(): any {
    const groupedData:any = {}

    if(this.selectedOffer) {
        this.selectedOffer.reservedOffers.forEach((offer:any) => {
          const tagId = offer.dailyMenuOffer.referenceFoodItem.tags[0].id
          const title = offer.dailyMenuOffer.referenceFoodItem.title
          const amount = offer.amount
          const group = offer.dailyMenuOffer.menuGroupSlot.tags.find((tag:any) => tag.id ===  tagId)
          const groupName = group && group.title ? group.title : ""
          if (!groupedData[groupName]) {
            groupedData[groupName] = {
              group: groupName
            }
          }
          if (!groupedData[groupName][title]) {
            groupedData[groupName][title] = {
              group: groupName,
              title: title,
              amount: amount
            }
          } else {
            groupedData[groupName][title].amount += amount
          }
        })
    } else {
      this.lastReservationDetailData.forEach((item:any) => {
        item.reservedOffers.forEach((offer: any) => {
          const tagId = offer.dailyMenuOffer.referenceFoodItem.tags[0].id
          const title = offer.dailyMenuOffer.referenceFoodItem.title
          const amount = offer.amount
          const group = item.topMenuGroup.subMenuGroups.find((group:any) => group.tags.some((tag:any) => tag.id === tagId))
          const groupName = group && group.title ? group.title : ""
          if (!groupedData[groupName]) {
            groupedData[groupName] = {
              group: groupName,
            }
          }
          if (!groupedData[groupName][title]) {
            groupedData[groupName][title] = {
              group: groupName,
              title: title,
              amount: amount
            };
          } else {
            groupedData[groupName][title].amount += amount
          }
        })
      })
    }

    return groupedData
  }

  closeDialog (): void {
    this.$emit('close')
  }

  sendRating (): void {
    if(this.selectedId === null) {
      this.createRatingForOrder()
    } else {
      this.onUpdateSelectedRating()
    }
  }

  get isDateTwoDaysBeforeToday(): boolean {
    let canBeRated:boolean = false

    if(this.lastReservationDetailData && this.lastReservationDetailData.length > 0) {
      const offerReservation = this.lastReservationDetailData[0]
      const deliveryTimeString = offerReservation.offerReservationDelivery?.createdAt

      if (deliveryTimeString) {
        const now = moment()
        const deliveryTime = moment(deliveryTimeString)
        const hoursSinceDelivery = now.diff(deliveryTime, 'hours')
        const delta = this.ratingDeliveryTimeData ?? 48

        if (delta && hoursSinceDelivery <= delta) {
          canBeRated = true
        }
      }
    }

    return canBeRated
  }

  async createRatingForOrder(): Promise<void> {
    let reservationIdList:any[] = []
    if(this.selectedOffer) {
      this.selectedOffer.reservedOffers!.forEach((item:any) => {
        item!.reservationIds.forEach((a:any) => {
          reservationIdList.push(`/api/food-ordering/offer-reservation/${a}`)
        })
      })
    } else {
      this.lastReservationDetailData.forEach((item:any) => {
        reservationIdList.push(`/api/food-ordering/offer-reservation/${item.id}`)
      })
    }

    const payload: any = {
      offerReservations: reservationIdList,
      value: this.ratingValue,
      note: this.description ? this.description : null
    }
    this.saveInProgress = true
    try {
      const response = await this.piniaStore.createNewRating(payload)
      this.isRatingFinished = true
      this.offerRating = response


    } catch (e) {
      this.snackbarColor = "error"
      this.snackBarText = this.$t('ratingDialog.errorDataCreate')
      this.snackbar = true
    } finally {
      this.saveInProgress = false
    }
  }

  async onUpdateSelectedRating(): Promise<void> {
    if(this.isDateTwoDaysBeforeToday) {
      const payload:RatingPayload = {
        id: this.selectedId,
        boarder: this.selectedOfferRating ? this.selectedOfferRating.boarder : this.ratingDataList[0].boarder,
        value: this.ratingValue,
        note: this.description ? this.description : null,
        offerReservations: this.selectedOfferRating ? this.selectedOfferRating.offerReservations : this.ratingDataList[0].offerReservations
      }
      this.saveInProgress = true
      try {
        const response = await this.piniaStore.updateSelectedRating(payload)
        this.offerRating = response
        this.isRatingFinished = true

      } catch (e) {
        this.snackbarColor = "error"
        this.snackBarText = this.$t('ratingDialog.errorDataChange')
        this.snackbar = true
      } finally {
        this.saveInProgress = false
      }
    }
  }

  created() {
    if(this.selectedOfferRating) {
      this.ratingValue = this.selectedOfferRating.value
      this.selectedId = this.selectedOfferRating.id
      this.description = this.selectedOfferRating.note ? this.selectedOfferRating.note : null
    } else if(this.selectedOffer) {
      let foundItem: RatingModel | undefined = this.ratingDataList.find((i:RatingModel) => i.offerReservations.includes(`/api/food-ordering/offer-reservation/${this.selectedOffer.id}`))
      this.ratingValue = foundItem ? foundItem.value : null
      this.selectedId = foundItem ? foundItem.id : null
      this.description = foundItem ? foundItem.note : null
      this.createdDate = foundItem ? foundItem.createdAt : ''
    }
  }

}
</script>

<style lang="stylus" scoped>
.icon-style
  font-size: 60px
.text-style
  background-color orange
  color white

</style>
